import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ROLE_LABELS } from './AdminDashboard.tsx';

interface AdminUser {
  id: string;
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_demo: boolean;
  created_at: string;
  referrer_code: string | null;
  referrer_name: string | null;
  has_pending_invitation: boolean;
}

export function AdminUsers() {
  const { user: me, startImpersonation } = useAuth();
  const isSuper = me?.role === 'SUPER_ADMIN';
  const [demoBase, setDemoBase] = useState('');
  const [demo, setDemo] = useState<{ key: string; role: string; email: string; password: string | null; created: boolean }[] | null>(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (role) params.set('role', role);
    if (search.trim()) params.set('q', search.trim());
    api.get<{ users: AdminUser[] }>(`/admin/users?${params}`).then((res) => setUsers(res.users));
  }, [role, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  async function createDemo(reset: boolean) {
    if (reset && !window.confirm('Générer de nouveaux mots de passe ? Les anciens ne fonctionneront plus.')) return;
    setDemoBusy(true);
    setError(null);
    try {
      const res = await api.post<{ accounts: NonNullable<typeof demo> }>('/admin/demo', { baseEmail: demoBase, reset });
      setDemo(res.accounts);
      load();
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'INVALID_BASE_EMAIL' ? 'Adresse e-mail invalide.' : 'Création impossible.');
    } finally {
      setDemoBusy(false);
    }
  }

  async function actAs(u: AdminUser) {
    setError(null);
    try {
      await startImpersonation(u.id);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'USER_SUSPENDED' ? 'Ce compte est suspendu.' : 'Connexion impossible.');
    }
  }

  async function toggle(u: AdminUser) {
    const verb = u.is_active ? 'suspendre' : 'réactiver';
    if (!window.confirm(`Voulez-vous ${verb} ${u.first_name} ${u.last_name} ?`)) return;
    setError(null);
    try {
      await api.post(`/admin/users/${u.id}/${u.is_active ? 'suspend' : 'activate'}`);
      load();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'Action réservée au Super Admin.' : 'Action impossible.');
    }
  }

  function statusBadge(u: AdminUser) {
    if (u.has_pending_invitation) return <span className="badge badge-pending">Invitation en attente</span>;
    return u.is_active ? <span className="badge badge-available">Actif</span> : <span className="badge badge-unavailable">Suspendu</span>;
  }

  return (
    <div>
      {isSuper && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 800 }}>Comptes de démonstration</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Un restaurateur, un extra et un apporteur d'affaires, avec des données d'exemple, pour qu'un associé teste l'application. Ils sont invisibles des vrais utilisateurs (et ne voient qu'entre eux).
            Les e-mails arrivent à l'adresse ci-dessous, sous la forme adresse+demo-rôle@domaine.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input className="input" style={{ maxWidth: 300 }} type="email" placeholder="Adresse qui reçoit les e-mails de démo" value={demoBase} onChange={(e) => setDemoBase(e.target.value)} />
            <button className="btn btn-primary btn-sm" disabled={demoBusy || !demoBase} onClick={() => createDemo(false)}>Créer ou compléter</button>
            <button className="btn btn-secondary btn-sm" disabled={demoBusy || !demoBase} onClick={() => createDemo(true)}>Nouveaux mots de passe</button>
          </div>
          {demo && (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Compte</th><th>E-mail</th><th>Mot de passe (affiché une seule fois)</th></tr></thead>
                <tbody>
                  {demo.map((a) => (
                    <tr key={a.key}>
                      <td style={{ fontWeight: 700 }}>{a.key === 'RESTAURATEUR' ? 'Restaurateur' : a.key === 'EXTRA' ? 'Extra' : 'Apporteur'}</td>
                      <td>{a.email}</td>
                      <td style={{ fontFamily: 'monospace' }}>{a.password ?? 'inchangé (déjà créé)'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Rechercher (nom, e-mail)" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input" style={{ maxWidth: 220 }} value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">Tous les rôles</option>
          {Object.entries(ROLE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

      {!users && <div className="empty-state">Chargement…</div>}
      {users && users.length === 0 && <div className="empty-state">Aucun utilisateur.</div>}
      {users && users.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Nom</th><th>E-mail</th><th>Rôle</th><th>Statut</th><th>Parrain</th><th>Inscrit le</th><th /></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 700 }}>{u.first_name} {u.last_name}{u.is_demo && <> <span className="badge badge-pending">Démo</span></>}</td>
                  <td>{u.email}</td>
                  <td>{ROLE_LABELS[u.role] ?? u.role}</td>
                  <td>{statusBadge(u)}</td>
                  <td>{u.referrer_name ? `${u.referrer_name} (${u.referrer_code})` : '—'}</td>
                  <td>{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {isSuper && u.id !== me?.id && u.is_active && ['EXTRA', 'ORG_OWNER', 'ORG_MANAGER', 'APPORTEUR'].includes(u.role) && (
                      <><button className="btn btn-secondary btn-sm" onClick={() => actAs(u)}>Se connecter en tant que</button>{' '}</>
                    )}
                    {u.id !== me?.id && !u.has_pending_invitation && (
                      <button className={`btn btn-sm ${u.is_active ? 'btn-danger-outline' : 'btn-secondary'}`} onClick={() => toggle(u)}>
                        {u.is_active ? 'Suspendre' : 'Réactiver'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
